import React, { useEffect, useState, useRef } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Stack,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MicNoneOutlinedIcon from "@mui/icons-material/MicNoneOutlined";
import RohanImg from "../assets/podcast_monster/rohan_png.png";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import SmartToyOutlinedIcon from "@mui/icons-material/SmartToyOutlined";
import TextareaAutosize from "@mui/material/TextareaAutosize";
import axios from "axios";
import { useTTSQueue } from "hooks/useTTSQueue";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeOffIcon from "@mui/icons-material/VolumeOff";

interface AIConciergePopupProps {
  open: boolean;
  onClose: () => void;
  courseid: string;
}

const AIConciergePopup: React.FC<AIConciergePopupProps> = ({ open, onClose, courseid, }) => {
  const [questionText, setQuestionText] = useState<string>("");
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [lastBotText, setLastBotText] = useState<string>("");
  const prevTextRef = useRef("");
  const [isRecordingQuestion, setIsRecordingQuestion] = useState(false);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<BlobPart[]>([]);
  const chatEndRef = useRef<HTMLDivElement | null>(null);

  const { playText, stopAll, isPlaying, audioRef, isMuted, setIsMuted } =
    useTTSQueue();

  const handleClose = () => {
    stopAll();
    onClose();
  };

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, isChatLoading]);

  const formatAIResponse = (text: string) => {
    if (!text) return "";
    let formatted = text;
    formatted = formatted.replace(/\*{2,}/g, "");
    formatted = formatted.replace(/#{1,}/g, "");
    formatted = formatted.replace(/^\* (.+)$/gm, "• $1");
    formatted = formatted.replace(
      /(?:^|\n)(• .*(?:\n• .*)*)/g,
      (match) => {
        const items = match
          .trim()
          .split("\n")
          .map((line) => `<li>${line.replace(/^• /, "")}</li>`)
          .join("");
        return `<ul>${items}</ul>`;
      }
    );
    formatted = formatted.replace(/^(.+):$/gm, "<h3>$1</h3>");
    formatted = formatted.replace(
      /^(?!<h3>|<ul>|<li>|<\/ul>)(.+)$/gm,
      "<p>$1</p>"
    );
    return formatted;
  };

  const stripHtml = (html: string) => {
    const tempDiv = document.createElement("div");
    tempDiv.innerHTML = html;
    return tempDiv.textContent || tempDiv.innerText || "";
  };

  const getChatBotResponse = async (query: string) => {
    if (!query.trim()) return;
    setChatMessages((prev) => [...prev, { role: "user", text: query }]);
    setQuestionText("");
    setIsChatLoading(true);
    try {
      const apiUrl = process.env.REACT_APP_SERVER_ENDPOINT || "";
      const res = await axios.post(`${apiUrl}/api/prompts/chat_answer`, {
        courseid: courseid || "698b705681424ab945f978e4",
        query,
        chapter: "",
      });
      let answer = res?.data?.response || "No response";
      const formatted = formatAIResponse(answer);
      setChatMessages((prev) => [...prev, { role: "bot", text: formatted }]);
      const cleanText = stripHtml(answer);
      stopAll();
      setLastBotText(cleanText);
    } catch (err) {
      console.error("Chat error", err);
    } finally {
      setIsChatLoading(false);
    }
  };

  useEffect(() => {
    if (!lastBotText) return;
    if (prevTextRef.current === lastBotText) return;
    prevTextRef.current = lastBotText;
    stopAll();
    playText(lastBotText);
  }, [lastBotText]);

  const startRecording = async (target: "question") => {
    if (!navigator.mediaDevices?.getUserMedia) return;
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mediaRecorder = new MediaRecorder(stream, { mimeType: "audio/webm" });
    audioChunksRef.current = [];
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) audioChunksRef.current.push(e.data);
    };
    mediaRecorder.onstop = () => sendAudioToBackend(target);
    mediaRecorder.start();
    mediaRecorderRef.current = mediaRecorder;
    setIsRecordingQuestion(true);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
    setIsRecordingQuestion(false);
  };

  const sendAudioToBackend = async (target: "question") => {
    const audioBlob = new Blob(audioChunksRef.current, { type: "audio/webm" });
    const formData = new FormData();
    formData.append("audio", audioBlob, "speech.webm");
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SPEECH_TO_TEXT_SERVICE}/speech_to_text`,
        { method: "POST", body: formData }
      );
      const data = await res.json();
      setQuestionText((prev) => `${prev} ${data.text}`.trim());
    } catch (err) {
      console.error("Speech upload failed", err);
    }
  };

  const toggleMute = () => {
    setIsMuted((prev) => {
      const newState = !prev;
      if (audioRef.current) {
        if (newState) audioRef.current.pause();
        else audioRef.current.play().catch(() => {});
      }
      return newState;
    });
  };

  /* ── Shared icon-button style ── */
  const iconBtnBase = {
    width: 38,
    height: 38,
    borderRadius: "10px",
    border: "1px solid rgba(255,255,255,0.1)",
    transition: "all 0.2s ease",
    "&:hover": { transform: "scale(1.06)" },
  };

  const isSpeakerAnimating = isPlaying && !isMuted;

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: { xs: "92%", sm: 460 },
          maxHeight: "94vh",
          display: "flex",
          flexDirection: "column",
          bgcolor: "#0D1321",
          borderRadius: "20px",
          border: "1px solid rgba(255,255,255,0.07)",
          overflow: "hidden",
          boxShadow: "0 32px 80px rgba(0,0,0,0.6)",
          color: "#fff",
        }}
      >
        {/* ── Header ── */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{
            px: 2.5,
            py: 1.5,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
            bgcolor: "#0D1321",
            flexShrink: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.2}>
            <Box
              sx={{
                width: 32,
                height: 32,
                borderRadius: "50%",
                background: "linear-gradient(135deg, #4BA5EB 0%, #1E5FA0 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SmartToyOutlinedIcon sx={{ fontSize: 17, color: "#fff" }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 14, fontWeight: 600, color: "#fff", lineHeight: 1.2 }}>
                Ask My AI Twin
              </Typography>
              <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.3 }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: "50%",
                    bgcolor: "#22c55e",
                    boxShadow: "0 0 6px #22c55e",
                  }}
                />
                <Typography sx={{ fontSize: 11, color: "#6b7280" }}>Online · Ready to help</Typography>
              </Stack>
            </Box>
          </Stack>

          <IconButton onClick={handleClose} size="small" sx={{ color: "#9ca3af", bgcolor: "rgba(255,255,255,0.05)", borderRadius: "8px", "&:hover": { bgcolor: "rgba(255,255,255,0.1)" } }}>
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Stack>

        {/* ── Hero / avatar ── */}
        <Box
          sx={{
            position: "relative",
            background: "linear-gradient(180deg, #131b2e 0%, #0D1321 100%)",
            flexShrink: 0,
            overflow: "hidden",
          }}
        >
          {/* Glow */}
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: "50%",
              transform: "translateX(-50%)",
              width: "80%",
              height: "100%",
              background: "radial-gradient(ellipse at 50% 0%, rgba(75,165,235,0.1) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />

          <Stack direction="row" alignItems="flex-end" sx={{ px: 2.5, pt: 2.5 }}>
            <Box
                sx={{
                    position: "relative",
                    width: { xs: 88, sm: 100 },
                    height: { xs: 108, sm: 120 },
                    flexShrink: 0,
                    overflow: "hidden",
                    borderRadius: "10px 10px 0 0",
                    border: "1px solid rgba(75,165,235,0.2)",
                    borderBottom: "none",
                }}
            >
                <Box
                    component="img"
                    src={RohanImg}
                    alt="Rohan Hall"
                    sx={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        objectPosition: "top",
                        display: "block",
                    }}
                />

                {/* Waveform bars — overlaid on the bottom of the image */}
                <Box
                    sx={{
                        display: isSpeakerAnimating ? "flex" : "none",
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        zIndex: 2,
                        alignItems: "center",
                        justifyContent: "center",
                        gap: "2px",
                        height: "28px",
                        pointerEvents: "none",
                        background: "linear-gradient(180deg, transparent 0%, rgba(0,0,0,0.4) 100%)",
                    }}
                >
                    {[4, 10, 16, 7, 20, 12, 18, 6, 14, 22, 9, 16].map((peak, i) => (
                        <Box
                            key={i}
                            sx={{
                                width: "2px",
                                minWidth: "2px",
                                borderRadius: "2px",
                                background: "#3B82F6",
                                animation: `waveStripPopup${i} 1.1s ease-in-out infinite`,
                                animationDelay: `${(i % 8) * 0.08}s`,
                                [`@keyframes waveStripPopup${i}`]: {
                                    "0%, 100%": { height: "4px" },
                                    "50%": { height: `${peak}px` },
                                },
                            }}
                        />
                    ))}
                </Box>
            </Box>
            <Box sx={{ pb: 2, pl: 2 }}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  display: "inline-flex",
                  bgcolor: "rgba(75,165,235,0.1)",
                  border: "1px solid rgba(75,165,235,0.22)",
                  borderRadius: "20px",
                  px: 1.2,
                  py: 0.4,
                  mb: 1,
                }}
              >
                <AutoAwesomeIcon sx={{ fontSize: 12, color: "#4BA5EB" }} />
                <Typography sx={{ fontSize: 10, color: "#4BA5EB", letterSpacing: "0.07em", textTransform: "uppercase" }}>
                  AI Concierge
                </Typography>
              </Stack>
              <Typography sx={{ fontSize: 17, fontWeight: 600, color: "#fff", mb: 0.5 }}>
                Rohan Hall
              </Typography>
              <Typography sx={{ fontSize: 12, color: "#6b7280", lineHeight: 1.5 }}>
                I am your personal AI Concierge. Ask me anything about the site.
              </Typography>
            </Box>
          </Stack>
        </Box>

        {/* ── Divider ── */}
        <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.06)", flexShrink: 0 }} />

        {/* ── Chat messages ── */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            px: 2.5,
            py: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            minHeight: 160,
            maxHeight: { xs: 200, sm: 260 },
            scrollbarWidth: "thin",
            scrollbarColor: "#1e293b transparent",
            "&::-webkit-scrollbar": { width: "4px" },
            "&::-webkit-scrollbar-thumb": { background: "#1e5fa0", borderRadius: "4px" },
          }}
        >
          {chatMessages.map((msg, i) =>
            msg.role === "user" ? (
              /* User bubble */
              <Box key={i} sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Box
                  sx={{
                    bgcolor: "#1E5FA0",
                    borderRadius: "12px 4px 12px 12px",
                    px: 1.8,
                    py: 1,
                    maxWidth: "80%",
                  }}
                >
                  <Typography sx={{ fontSize: 13, color: "#fff", lineHeight: 1.6 }}>
                    {msg.text}
                  </Typography>
                </Box>
              </Box>
            ) : (
              /* Bot bubble */
              <Box key={i} sx={{ display: "flex", gap: 1, alignItems: "flex-start", maxWidth: "90%" }}>
                <Box
                  component="img"
                  src={RohanImg}
                  alt="Rohan"
                  sx={{
                    width: 26,
                    height: 26,
                    borderRadius: "50%",
                    objectFit: "cover",
                    objectPosition: "top",
                    border: "1px solid rgba(75,165,235,0.25)",
                    flexShrink: 0,
                    mt: 0.3,
                  }}
                />
                <Box
                  sx={{
                    bgcolor: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.07)",
                    borderRadius: "4px 12px 12px 12px",
                    px: 1.8,
                    py: 1,
                    "& h3": { fontSize: 14, fontWeight: 600, color: "#fff", m: "5px 0" },
                    "& p": { fontSize: 13, color: "#d1d5db", m: "3px 0", lineHeight: 1.6 },
                    "& ul": { pl: "16px", m: "3px 0" },
                    "& li": { fontSize: 13, color: "#d1d5db", mb: "2px" },
                  }}
                  dangerouslySetInnerHTML={{ __html: msg.text }}
                />
              </Box>
            )
          )}

          {/* Typing indicator */}
          {isChatLoading && (
            <Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", maxWidth: 80 }}>
              <Box
                component="img"
                src={RohanImg}
                alt="Rohan"
                sx={{
                  width: 26,
                  height: 26,
                  borderRadius: "50%",
                  objectFit: "cover",
                  objectPosition: "top",
                  border: "1px solid rgba(75,165,235,0.25)",
                  flexShrink: 0,
                }}
              />
              <Box
                sx={{
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.07)",
                  borderRadius: "4px 12px 12px 12px",
                  px: 1.8,
                  py: 1.2,
                  display: "flex",
                  gap: "5px",
                  alignItems: "center",
                }}
              >
                {[0, 0.2, 0.4].map((delay, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      width: 6,
                      height: 6,
                      borderRadius: "50%",
                      bgcolor: "#4BA5EB",
                      animation: "dotBlink 1.2s ease-in-out infinite",
                      animationDelay: `${delay}s`,
                      "@keyframes dotBlink": {
                        "0%, 100%": { opacity: 0.25, transform: "scale(0.8)" },
                        "50%": { opacity: 1, transform: "scale(1)" },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          )}

          <div ref={chatEndRef} />
        </Box>

        {/* ── Divider ── */}
        <Box sx={{ height: "1px", bgcolor: "rgba(255,255,255,0.06)", flexShrink: 0 }} />

        {/* ── Input area ── */}
        <Box sx={{ px: 2, py: 1.5, bgcolor: "#0D1321", flexShrink: 0 }}>
          <Box
            sx={{
              bgcolor: "rgba(255,255,255,0.03)",
              border: "1px solid rgba(255,255,255,0.09)",
              borderRadius: "14px",
              p: "10px 12px",
              display: "flex",
              flexDirection: "column",
              gap: 1,
              transition: "border-color 0.2s",
              "&:focus-within": { borderColor: "rgba(75,165,235,0.4)" },
            }}
          >
            <TextareaAutosize
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  getChatBotResponse(questionText);
                }
              }}
              placeholder="Ask me anything about the site."
              minRows={2}
              style={{
                width: "100%",
                background: "transparent",
                color: "#e5e7eb",
                border: "none",
                fontSize: "13px",
                fontFamily: "inherit",
                resize: "none",
                outline: "none",
                lineHeight: 1.6,
                boxSizing: "border-box",
              }}
            />

            {/* Action row */}
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              {/* Left: mute + mic */}
              <Stack direction="row" spacing={0.8}>
                {/* Mute / unmute */}
                <IconButton
                  onClick={toggleMute}
                  title={isMuted ? "Unmute" : "Mute"}
                  sx={{
                    ...iconBtnBase,
                    //bgcolor: isMuted ? "rgba(255,255,255,0.05)" : "rgba(75,165,235,0.12)",
                    background: isMuted
                      ? "linear-gradient(135deg, #7C3AED 0%, #60A5FA 100%)"
                      : "linear-gradient(135deg, #4F46E5 0%, #60A5FA 100%)",
                    borderColor: isMuted ? "rgba(255,255,255,0.1)" : "rgba(75,165,235,0.25)",
                    boxShadow: !isMuted
                      ? "0 0 25px rgba(96,165,250,0.6)"
                      : "0 8px 24px rgba(96,165,250,0.25)",
                    //color: isMuted ? "#6b7280" : "#4BA5EB",
                  }}
                >
                  {isMuted ? (
                    <VolumeOffIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <VolumeUpIcon sx={{ fontSize: 18 }} />
                  )}
                </IconButton>

                {/* Mic */}
                <Box
                  sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",

                    "&::before": isRecordingQuestion
                      ? {
                          content: '""',
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          border: "2px solid rgba(96,165,250,0.7)",
                          animation: "recordRipple 1.8s infinite",
                        }
                      : {},

                    "&::after": isRecordingQuestion
                      ? {
                          content: '""',
                          position: "absolute",
                          width: "100%",
                          height: "100%",
                          borderRadius: "50%",
                          border: "2px solid rgba(96,165,250,0.5)",
                          animation: "recordRipple 1.8s infinite 0.9s",
                        }
                      : {},

                    "@keyframes recordRipple": {
                      "0%": {
                        transform: "scale(1)",
                        opacity: 0.7,
                      },
                      "100%": {
                        transform: "scale(2.2)",
                        opacity: 0,
                      },
                    },
                  }}
                >
                  <IconButton
                    onClick={() =>
                      isRecordingQuestion
                        ? stopRecording()
                        : startRecording("question")
                    }
                    title={
                      isRecordingQuestion
                        ? "Stop recording"
                        : "Record voice"
                    }
                    sx={{
                      ...iconBtnBase,
                      position: "relative",
                      zIndex: 2,
                      borderRadius: "50px",

                      background: isRecordingQuestion
                        ? "linear-gradient(135deg, #7C3AED 0%, #60A5FA 100%)"
                        : "linear-gradient(135deg, #4F46E5 0%, #60A5FA 100%)",

                      boxShadow: isRecordingQuestion
                        ? "0 0 25px rgba(96,165,250,0.6)"
                        : "0 8px 24px rgba(96,165,250,0.25)",

                      transition: "all 0.25s ease",
                    }}
                  >
                    <MicNoneOutlinedIcon
                      sx={{
                        fontSize: 20,
                        color: "#fff",
                      }}
                    />
                  </IconButton>
                </Box>
              </Stack>

              {/* Send */}
              <IconButton
                onClick={() => getChatBotResponse(questionText)}
                disabled={!questionText.trim() || isChatLoading}
                title="Send"
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: "10px",
                  bgcolor: questionText.trim() ? "#1E5FA0" : "rgba(255,255,255,0.05)",
                  border: "1px solid",
                  borderColor: questionText.trim() ? "#2573be" : "rgba(255,255,255,0.08)",
                  color: questionText.trim() ? "#fff" : "#4b5563",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    bgcolor: questionText.trim() ? "#2573be" : undefined,
                    transform: questionText.trim() ? "scale(1.06)" : undefined,
                  },
                  "&:disabled": { opacity: 0.5 },
                }}
              >
                <SendIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </Stack>
          </Box>

          {/* Hint */}
          <Typography sx={{ fontSize: 11, color: "#374151", textAlign: "center", mt: 1 }}>
            Press Enter to send · Shift+Enter for new line
          </Typography>
        </Box>
      </Box>
    </Modal>
  );
};

export default AIConciergePopup;